<template>
  <div>
    <v-alert v-if="error" type="error" dense class="mb-4">
      {{ error }}
    </v-alert>

    <v-row v-if="funnel" dense class="mb-4">
      <v-col
        v-for="card in summaryCards"
        :key="card.label"
        cols="6"
        sm="4"
        md="2"
      >
        <v-card outlined class="pa-3 text-center">
          <div class="text-caption grey--text">
            {{ card.label }}
          </div>
          <div class="text-h6 font-weight-bold">
            {{ card.value }}
          </div>
          <div v-if="card.hint" class="text-caption grey--text">
            {{ card.hint }}
          </div>
        </v-card>
      </v-col>
    </v-row>

    <v-card v-if="funnel" outlined class="mb-5 pa-4">
      <div class="text-subtitle-2 mb-3">
        По каналам
      </div>
      <v-simple-table dense>
        <template #default>
          <thead>
            <tr>
              <th class="text-left">
                Канал
              </th>
              <th class="text-right">
                Отправлено
              </th>
              <th class="text-right">
                Переходы
              </th>
              <th class="text-right">
                Покупки
              </th>
              <th class="text-right">
                Конверсия
              </th>
              <th class="text-right">
                Сумма
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in funnel.channels" :key="row.channel">
              <td>{{ row.label }}</td>
              <td class="text-right">
                {{ row.sent }}
              </td>
              <td class="text-right">
                {{ row.opened }}
              </td>
              <td class="text-right">
                {{ row.purchased }}
              </td>
              <td class="text-right">
                {{ row.conversion }}%
              </td>
              <td class="text-right">
                {{ formatMoney(row.revenue) }} ₽
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <p class="text-caption grey--text mb-0 mt-2">
        Покупка учитывается в каждом канале, куда предложение было доставлено.
      </p>
    </v-card>

    <v-card outlined class="mb-4 pa-4">
      <v-row dense>
        <v-col cols="12" sm="4" md="3">
          <v-text-field
            v-model="filters.search"
            label="Поиск"
            dense
            outlined
            hide-details
            clearable
            @keydown.enter="applyFilters"
          />
        </v-col>
        <v-col cols="12" sm="4" md="2">
          <v-select
            v-model="filters.status"
            :items="statusItems"
            label="Статус"
            dense
            outlined
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="12" sm="4" md="2">
          <v-select
            v-model="filters.opened_via"
            :items="channelItems"
            label="Источник перехода"
            dense
            outlined
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="12" sm="4" md="2">
          <v-select
            v-model="filters.channel"
            :items="channelItems"
            label="Канал доставки"
            dense
            outlined
            hide-details
            clearable
          />
        </v-col>
        <v-col cols="12" sm="4" md="3" class="d-flex align-center">
          <v-btn color="primary" class="mr-2" :loading="loading" @click="applyFilters">
            Применить
          </v-btn>
          <v-btn text :disabled="loading" @click="resetFilters">
            Сбросить
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-card>
      <v-data-table
        :headers="headers"
        :items="items"
        :loading="loading"
        :items-per-page="pageSize"
        hide-default-footer
        mobile-breakpoint="0"
        class="elevation-0 renewal-offers-table"
      >
        <template #[`item.created_at`]="{ item }">
          <span class="text-no-wrap">{{ formatDateTime(item.created_at) }}</span>
        </template>
        <template #[`item.user`]="{ item }">
          <div class="font-weight-medium">
            {{ item.user_name || `ID ${item.user_id}` }}
          </div>
          <div class="text-caption text--secondary text-no-wrap">
            {{ item.user_phone || '—' }}
          </div>
          <div class="text-caption text--secondary text-truncate" style="max-width: 180px">
            {{ item.user_email || '—' }}
          </div>
        </template>
        <template #[`item.title`]="{ item }">
          <v-tooltip bottom max-width="420">
            <template #activator="{ on, attrs }">
              <div
                v-bind="attrs"
                class="title-cell"
                v-on="on"
              >
                {{ item.title }}
              </div>
            </template>
            <span>{{ item.title }}</span>
          </v-tooltip>
        </template>
        <template #[`item.status_label`]="{ item }">
          <span class="text-no-wrap">{{ item.status_label }}</span>
        </template>
        <template #[`item.offer_price`]="{ item }">
          <span class="text-no-wrap font-weight-medium">{{ formatMoney(item.offer_price) }}&nbsp;₽</span>
        </template>
        <template #[`item.savings_amount`]="{ item }">
          <div v-if="item.savings_amount" class="text-no-wrap">
            {{ formatMoney(item.savings_amount) }}&nbsp;₽
          </div>
          <div v-if="item.savings_per_visit" class="text-caption text--secondary text-no-wrap">
            {{ formatMoney(item.savings_per_visit) }}&nbsp;₽ / зан.
          </div>
          <span v-if="!item.savings_amount">—</span>
        </template>
        <template #[`item.deliveries`]="{ item }">
          <div v-if="item.deliveries.length" class="delivery-cell">
            <div
              v-for="delivery in item.deliveries"
              :key="`${item.id}-${delivery.channel}`"
              class="text-no-wrap"
            >
              {{ channelLabel(delivery.channel) }}:
              <span :class="deliveryStatusClass(delivery.status)">
                {{ deliveryStatusLabel(delivery.status) }}
              </span>
            </div>
          </div>
          <span v-else>—</span>
        </template>
        <template #[`item.opened_via`]="{ item }">
          <span class="text-no-wrap">{{ channelLabel(item.opened_via) || '—' }}</span>
        </template>
        <template #[`item.opened_at`]="{ item }">
          <span class="text-no-wrap">{{ formatDateTime(item.opened_at) }}</span>
        </template>
        <template #[`item.purchased_at`]="{ item }">
          <span class="text-no-wrap">{{ formatDateTime(item.purchased_at) }}</span>
        </template>
      </v-data-table>

      <div class="d-flex align-center justify-space-between pa-4">
        <div class="text-caption grey--text">
          Всего: {{ total }}
        </div>
        <v-pagination
          v-model="page"
          :length="pageCount"
          :total-visible="7"
          @input="loadOffers"
        />
      </div>
    </v-card>
  </div>
</template>

<script>
import Api from '@/api/api'

export default {
  name: 'RenewalOffers',
  data() {
    return {
      loading: false,
      error: '',
      funnel: null,
      items: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: {
        search: '',
        status: '',
        opened_via: '',
        channel: '',
      },
      statusItems: [
        { text: 'создано', value: 'created' },
        { text: 'отправлено', value: 'sent' },
        { text: 'не отправлено', value: 'send_failed' },
        { text: 'переход', value: 'opened' },
        { text: 'покупка', value: 'purchased' },
      ],
      channelItems: [
        { text: 'Telegram', value: 'telegram' },
        { text: 'MAX', value: 'max' },
        { text: 'Email', value: 'email' },
        { text: 'ПланФикс', value: 'planfix' },
      ],
      headers: [
        {
          text: 'Создано', value: 'created_at', width: '120px',
        },
        {
          text: 'Клиент', value: 'user', width: '180px', sortable: false,
        },
        {
          text: 'Абонемент', value: 'title', width: '260px',
        },
        {
          text: 'Статус', value: 'status_label', width: '120px',
        },
        {
          text: 'Цена', value: 'offer_price', width: '100px', align: 'end',
        },
        {
          text: 'Экономия', value: 'savings_amount', width: '120px', align: 'end',
        },
        {
          text: 'Доставка', value: 'deliveries', width: '140px', sortable: false,
        },
        {
          text: 'Источник', value: 'opened_via', width: '100px',
        },
        {
          text: 'Переход', value: 'opened_at', width: '120px',
        },
        {
          text: 'Покупка', value: 'purchased_at', width: '120px',
        },
      ],
    }
  },
  computed: {
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.pageSize))
    },
    summaryCards() {
      if (!this.funnel) return []
      return [
        { label: 'Создано', value: this.funnel.created },
        { label: 'Отправлено', value: this.funnel.sent },
        { label: 'Доставлено', value: this.funnel.delivered },
        { label: 'Переходы', value: this.funnel.opened },
        { label: 'Покупки', value: this.funnel.purchased },
        {
          label: 'Конверсия',
          value: `${this.funnel.conversion}%`,
          hint: `${this.formatMoney(this.funnel.revenue)} ₽`,
        },
      ]
    },
  },
  created() {
    this.load()
  },
  methods: {
    async load() {
      await Promise.all([this.loadFunnel(), this.loadOffers()])
    },
    async loadFunnel() {
      try {
        const res = await Api.getRenewalOffersFunnel()
        if (res && res.success) {
          this.funnel = res.data
        }
      } catch (e) {
        this.error = e.response?.data?.error || 'Не удалось загрузить воронку'
      }
    },
    async loadOffers() {
      this.loading = true
      this.error = ''
      try {
        const params = {
          page: this.page,
          page_size: this.pageSize,
        }
        if (this.filters.search) params.search = this.filters.search
        if (this.filters.status) params.status = this.filters.status
        if (this.filters.opened_via) params.opened_via = this.filters.opened_via
        if (this.filters.channel) params.channel = this.filters.channel

        const res = await Api.getRenewalOffersAdmin(params)
        if (res && res.success) {
          this.items = res.data.items || []
          this.total = res.data.total || 0
        } else {
          this.error = res?.error || 'Не удалось загрузить предложения'
        }
      } catch (e) {
        this.error = e.response?.data?.error || 'Не удалось загрузить предложения'
      } finally {
        this.loading = false
      }
    },
    applyFilters() {
      this.page = 1
      this.loadOffers()
    },
    resetFilters() {
      this.filters = {
        search: '',
        status: '',
        opened_via: '',
        channel: '',
      }
      this.page = 1
      this.loadOffers()
    },
    formatMoney(value) {
      const number = Number(value || 0)
      return `${number}`.replace(/(\d)(?=(\d{3})+([^\d]|$))/g, '$1\u00a0')
    },
    formatDateTime(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return value
      const day = date.toLocaleDateString('ru-RU')
      const time = date.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
      })
      return `${day} ${time}`
    },
    channelLabel(channel) {
      if (!channel) return ''
      const found = this.channelItems.find(item => item.value === channel)
      return found ? found.text : channel
    },
    deliveryStatusLabel(status) {
      const map = {
        sent: 'отправлено',
        failed: 'ошибка',
        pending: 'ожидает',
      }
      return map[status] || status
    },
    deliveryStatusClass(status) {
      if (status === 'sent') return 'success--text'
      if (status === 'failed') return 'error--text'
      return 'text--secondary'
    },
  },
}
</script>

<style scoped>
.renewal-offers-table ::v-deep .v-data-table__wrapper {
  overflow-x: auto;
}

.renewal-offers-table ::v-deep table {
  min-width: 1280px;
}

.renewal-offers-table ::v-deep td,
.renewal-offers-table ::v-deep th {
  vertical-align: top !important;
  color: rgba(0, 0, 0, 0.87);
}

.title-cell {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 260px;
  line-height: 1.35;
}

.delivery-cell {
  line-height: 1.4;
}
</style>
